import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SocialAsset, SocialAssetVariant, SocialAssetsGenerateRequest } from "@shared/api";
import { apiFetch, toAbsoluteAssetUrl } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

export type SocialPresetId = "tpt_clean" | "tpt_warm" | "tpt_contrast";

const PRESETS: Array<{ id: SocialPresetId; label: string; description: string }> = [
  { id: "tpt_clean", label: "TPT Clean", description: "Semleges, világos, természetes színek." },
  { id: "tpt_warm", label: "TPT Warm", description: "Melegebb tónus, barátságos hangulat." },
  { id: "tpt_contrast", label: "TPT Contrast", description: "Erősebb kontraszt, ütősebb megjelenés." },
];

const VARIANTS: Array<{ id: SocialAssetVariant; label: string; hint: string }> = [
  { id: "ig_feed_square", label: "IG feed 1:1", hint: "1080×1080" },
  { id: "ig_feed_portrait", label: "IG feed 4:5", hint: "1080×1350" },
  { id: "ig_story", label: "IG story 9:16", hint: "1080×1920" },
  { id: "fb_feed", label: "FB feed", hint: "1200×630" },
];

export function SocialComposer(props: {
  photoIds: string[];
  onGenerated: (assets: SocialAsset[]) => void;
}) {
  const { t } = useTranslation();
  const [preset, setPreset] = useState<SocialPresetId>("tpt_clean");
  const [variants, setVariants] = useState<Set<SocialAssetVariant>>(
    new Set<SocialAssetVariant>(["ig_feed_square", "ig_story"])
  );
  const [logoEnabled, setLogoEnabled] = useState(true);
  const [logoPosition, setLogoPosition] = useState<"top_left" | "top_right" | "bottom_left" | "bottom_right">(
    "bottom_right"
  );
  const [logoScale, setLogoScale] = useState("0.14");
  const [headline, setHeadline] = useState("");
  const [cta, setCta] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedVariantList = useMemo(() => Array.from(variants), [variants]);

  const canGenerate = props.photoIds.length > 0 && selectedVariantList.length > 0 && !busy;

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <div className="text-sm text-muted-foreground">
          {props.photoIds.length} {t("images.selected")}
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t("social.preset")}</Label>
        <Select value={preset} onValueChange={(v: SocialPresetId) => setPreset(v)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRESETS.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                <div className="flex flex-col">
                  <span className="font-medium">{p.label}</span>
                  <span className="text-xs text-muted-foreground">{p.description}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>{t("social.formats")}</Label>
        <div className="grid grid-cols-2 gap-2">
          {VARIANTS.map((v) => {
            const checked = variants.has(v.id);
            return (
              <button
                key={v.id}
                type="button"
                className={cn(
                  "rounded-md border px-3 py-2 text-left text-sm hover:bg-muted transition-colors",
                  checked && "ring-2 ring-primary/40"
                )}
                onClick={() =>
                  setVariants((prev) => {
                    const next = new Set(prev);
                    if (next.has(v.id)) next.delete(v.id);
                    else next.add(v.id);
                    return next;
                  })
                }
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{v.label}</span>
                  <span className="text-xs text-muted-foreground">{v.hint}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Checkbox id="logoEnabled" checked={logoEnabled} onCheckedChange={(v) => setLogoEnabled(!!v)} />
          <label htmlFor="logoEnabled" className="text-sm font-medium cursor-pointer">
            {t("social.logo")}
          </label>
        </div>
        {logoEnabled && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>{t("social.logoPosition")}</Label>
              <Select value={logoPosition} onValueChange={(v: any) => setLogoPosition(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="top_left">{t("social.posTopLeft")}</SelectItem>
                  <SelectItem value="top_right">{t("social.posTopRight")}</SelectItem>
                  <SelectItem value="bottom_left">{t("social.posBottomLeft")}</SelectItem>
                  <SelectItem value="bottom_right">{t("social.posBottomRight")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("social.logoScale")}</Label>
              <Input value={logoScale} onChange={(e) => setLogoScale(e.target.value)} placeholder="0.14" />
              <p className="text-xs text-muted-foreground">{t("social.logoScaleHint")}</p>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="space-y-2">
          <Label>{t("social.headline")}</Label>
          <Input value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder={t("social.headlinePh")} />
        </div>
        <div className="space-y-2">
          <Label>{t("social.cta")}</Label>
          <Input value={cta} onChange={(e) => setCta(e.target.value)} placeholder={t("social.ctaPh")} />
        </div>
        <div className="space-y-2">
          <Label>{t("social.caption")}</Label>
          <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={t("social.captionPh")} />
        </div>
      </div>

      {error && <div className="text-sm text-destructive">{error}</div>}

      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="default"
          disabled={!canGenerate}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const req: SocialAssetsGenerateRequest = {
                photoIds: props.photoIds,
                variants: selectedVariantList,
                preset,
                logo: logoEnabled
                  ? { enabled: true, position: logoPosition, scale: Number(logoScale) || 0.14 }
                  : { enabled: false, position: "bottom_right", scale: 0.14 },
                overlayText: { headline: headline.trim() || undefined, cta: cta.trim() || undefined },
                caption: caption.trim() || null,
              };
              const r = await apiFetch("/api/social/assets/generate", { method: "POST", body: JSON.stringify(req) });
              if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                throw new Error(err?.userMessage || err?.message || "Generate failed");
              }
              const data = (await r.json()) as { assets?: SocialAsset[] };
              props.onGenerated(data.assets || []);
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {t("social.generating")}
            </>
          ) : (
            t("social.generate")
          )}
        </Button>
      </div>
    </div>
  );
}

export function SocialAssetsPreview(props: { assets: SocialAsset[] }) {
  const { t } = useTranslation();
  if (props.assets.length === 0) return null;
  return (
    <div className="space-y-2">
      <Label>{t("social.generatedAssets")}</Label>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {props.assets.map((a) => (
          <div key={a.id} className="rounded border overflow-hidden">
            <div className="aspect-square bg-muted">
              <img
                src={toAbsoluteAssetUrl(a.file_url)}
                alt={a.variant}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="p-2 text-xs text-muted-foreground flex items-center justify-between">
              <span>{a.variant}</span>
              <span>{a.status}</span>
            </div>
            <div className="p-2 pt-0 flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  await apiFetch(`/api/social/assets/${a.id}/approve`, { method: "POST" });
                }}
              >
                {t("social.approve")}
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={async () => {
                  await apiFetch(`/api/social/assets/${a.id}/publish`, { method: "POST" });
                }}
              >
                {t("social.publish")}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

